/**
 * Unified Zoom Configuration
 * Re-exports from centralized /src/config/zoomConfig.ts
 *
 * All zoom constants are now defined in /src/config/zoomConfig.ts.
 * This file re-exports them so existing imports remain valid.
 */

import {
    UNIFIED_ZOOM_CONSTANTS,
    ZOOM_FONT_SIZE_CONFIG,
} from '../../../../../config/zoomConfig';

// Re-export centralized constants
export { UNIFIED_ZOOM_CONSTANTS, ZOOM_FONT_SIZE_CONFIG };

/**
 * Zoom configuration interface
 */
export interface ZoomConfig {
    min: number;
    max: number;
    step: number;
    default: number;
}

/**
 * Unified zoom configuration using UNIFIED_ZOOM_CONSTANTS
 * Replaces all previous DEFAULT_ZOOM_CONFIG instances
 */
export const UNIFIED_ZOOM_CONFIG: ZoomConfig = {
    min: UNIFIED_ZOOM_CONSTANTS.MIN_ZOOM,
    max: UNIFIED_ZOOM_CONSTANTS.MAX_ZOOM,
    step: UNIFIED_ZOOM_CONSTANTS.STEP,
    default: UNIFIED_ZOOM_CONSTANTS.DEFAULT_ZOOM
};

/**
 * Zoom behavior mode types
 */
export type ZoomBehaviorMode = 'fixed' | 'fit';

/**
 * Zoom behavior configuration interface
 */
export interface ZoomBehaviorConfig {
    mode: ZoomBehaviorMode;          // Default zoom behavior mode
    fixedZoomLevel: number;          // Fixed zoom level when mode is 'fixed'
    fitPadding: number;              // Padding for 'fit' mode (percentage)
    minZoom: number;                 // Minimum allowed zoom level
    maxZoom: number;                 // Maximum allowed zoom level
    enableUserZoom: boolean;         // Allow user to zoom manually
    resetOnDataChange: boolean;      // Reset zoom when chart data changes
}

/**
 * Default zoom behavior configuration
 */
export const DEFAULT_ZOOM_BEHAVIOR_CONFIG: ZoomBehaviorConfig = {
    mode: 'fit',                                      // Auto-fit chart to container by default
    fixedZoomLevel: UNIFIED_ZOOM_CONSTANTS.FIXED_ZOOM_LEVEL,  // Use centralized fixed zoom
    fitPadding: UNIFIED_ZOOM_CONSTANTS.FIT_PADDING,           // Use centralized fit padding
    minZoom: UNIFIED_ZOOM_CONSTANTS.MIN_ZOOM,                 // Use centralized minimum zoom
    maxZoom: UNIFIED_ZOOM_CONSTANTS.MAX_ZOOM,                 // Use centralized maximum zoom
    enableUserZoom: true,               // Allow user zoom interaction
    resetOnDataChange: true             // Reset zoom when chart data changes
};

/**
 * Zoom display ranges for different modes
 */
export const ZOOM_RANGES = {
    SYMBOLS: { min: 0, max: UNIFIED_ZOOM_CONSTANTS.SYMBOLS_THRESHOLD },
    SHORT: { min: UNIFIED_ZOOM_CONSTANTS.SYMBOLS_THRESHOLD, max: UNIFIED_ZOOM_CONSTANTS.FULL_THRESHOLD },
    FULL: { min: UNIFIED_ZOOM_CONSTANTS.FULL_THRESHOLD, max: UNIFIED_ZOOM_CONSTANTS.MAX_ZOOM }
} as const;

/**
 * Display mode type
 */
export type DisplayMode = 'symbols' | 'short' | 'full';

/**
 * Get display mode based on zoom level
 */
export function getDisplayModeForZoom(zoomLevel: number): DisplayMode {
    if (zoomLevel <= UNIFIED_ZOOM_CONSTANTS.SYMBOLS_THRESHOLD) {
        return 'symbols';
    } else if (zoomLevel <= UNIFIED_ZOOM_CONSTANTS.FULL_THRESHOLD) {
        return 'short';
    } else {
        return 'full';
    }
}

/**
 * Clamp zoom value within valid range
 * Can use global constants or custom zoom behavior config
 */
export function clampZoom(zoom: number, zoomBehavior?: ZoomBehaviorConfig): number {
    if (zoomBehavior) {
        return Math.max(zoomBehavior.minZoom, Math.min(zoom, zoomBehavior.maxZoom));
    }
    return Math.max(
        UNIFIED_ZOOM_CONSTANTS.MIN_ZOOM,
        Math.min(zoom, UNIFIED_ZOOM_CONSTANTS.MAX_ZOOM)
    );
}

/**
 * Calculate initial zoom level based on container size
 * Simple 2-parameter version for basic usage
 */
export function calculateInitialZoom(containerWidth: number, containerHeight: number): number;

/**
 * Calculate initial zoom level with zoom behavior configuration
 * Advanced 3-parameter version for sophisticated zoom calculation
 */
export function calculateInitialZoom(
    containerBounds: { width: number; height: number },
    chartBounds: { width: number; height: number },
    zoomBehavior: ZoomBehaviorConfig
): number;

export function calculateInitialZoom(
    containerWidthOrBounds: number | { width: number; height: number },
    containerHeightOrChartBounds: number | { width: number; height: number },
    zoomBehavior?: ZoomBehaviorConfig
): number {
    // Handle simple 2-parameter usage
    if (typeof containerWidthOrBounds === 'number' && typeof containerHeightOrChartBounds === 'number') {
        const minDimension = Math.min(containerWidthOrBounds, containerHeightOrChartBounds);
        const baseSize = 500; // Reference container size
        const calculatedZoom = minDimension / baseSize;
        return clampZoom(calculatedZoom);
    }

    // Handle advanced 3-parameter usage with zoom behavior
    const containerBounds = containerWidthOrBounds as { width: number; height: number };
    const chartBounds = containerHeightOrChartBounds as { width: number; height: number };
    const config = zoomBehavior!;

    if (config.mode === 'fixed') {
        // Return the fixed zoom level, clamped within bounds
        return Math.max(config.minZoom, Math.min(config.fixedZoomLevel, config.maxZoom));
    } else {
        // Fit mode: calculate zoom to fit chart in container with padding
        const paddingFactor = (100 - config.fitPadding) / 100;
        const availableWidth = containerBounds.width * paddingFactor;
        const availableHeight = containerBounds.height * paddingFactor;

        const scaleX = availableWidth / chartBounds.width;
        const scaleY = availableHeight / chartBounds.height;
        const calculatedZoom = Math.min(scaleX, scaleY);

        // Clamp within configured bounds
        return Math.max(config.minZoom, Math.min(calculatedZoom, config.maxZoom));
    }
}

/**
 * Get zoom range information for a specific mode
 */
export function getZoomRangeInfo(mode: DisplayMode) {
    switch (mode) {
        case 'symbols':
            return ZOOM_RANGES.SYMBOLS;
        case 'short':
            return ZOOM_RANGES.SHORT;
        case 'full':
            return ZOOM_RANGES.FULL;
        default:
            return ZOOM_RANGES.SHORT;
    }
}

/**
 * Get descriptive text for display mode
 */
export function getZoomModeDescription(mode: DisplayMode): string {
    switch (mode) {
        case 'symbols':
            return 'Symbol Mode: Compact astronomical symbols (\u2609\u263D\u2642\u263F\u2643\u2640\u2644\u260A\u260B)';
        case 'short':
            return 'Short Mode: Abbreviated names (Sy Ch Ma Bu Gu Sk Sa Ra Ke)';
        case 'full':
            return 'Full Mode: Complete Sanskrit names (Surya Chandra Mangala...)';
        default:
            return 'Unknown zoom mode';
    }
}

/**
 * Get descriptive text for zoom behavior mode
 */
export function getZoomBehaviorDescription(mode: ZoomBehaviorMode): string {
    switch (mode) {
        case 'fixed':
            return 'Fixed Mode: Uses a fixed zoom level specified in configuration';
        case 'fit':
            return 'Fit Mode: Automatically calculates zoom to fit content with padding';
        default:
            return 'Unknown zoom behavior mode';
    }
}

/**
 * Validate zoom configuration
 */
export function validateZoomConfig(config: ZoomConfig): boolean {
    return !!(
        config.min >= 0 &&
        config.max > config.min &&
        config.step > 0 &&
        config.default >= config.min &&
        config.default <= config.max
    );
}

// Export aliases for consistency
export const ZOOM_CONSTANTS = UNIFIED_ZOOM_CONSTANTS;
export { UNIFIED_ZOOM_CONFIG as DEFAULT_ZOOM_CONFIG };
